// src/components/TicketButtons/HrInquiryButton.tsx
import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { createHrInquiry, HrInquiryPayload } from '../../api/ticketingApi';
import { useTranslation } from 'react-i18next';

type Props = {
  buttonText?: string;
  onSuccess?: (ticketId: string) => void;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'inherit' | 'success' | 'error' | 'info' | 'warning';
  size?: 'small' | 'medium' | 'large';
  /**
   * Estilos del botón:
   * - "default": estilo verde original del proyecto.
   * - "appbar": estilo para barra negra (texto claro).
   * - "firmas": replica la pastilla amarilla del botón "Firmas".
   */
  appearance?: 'default' | 'appbar' | 'firmas';
};

export default function HrInquiryButton({
  buttonText,
  onSuccess,
  variant = 'outlined',
  color = 'primary',
  size = 'small',
  appearance = 'default',
}: Props) {
  const { t } = useTranslation<'translation'>();
  const defaultButtonText = t('tickets.hrInquiry.button');
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [snack, setSnack] = React.useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error';
  }>({ open: false, msg: '', sev: 'success' });

  const [type, setType] = React.useState<HrInquiryPayload['type']>('duda');
  const [comment, setComment] = React.useState<string>('');
  const [requesterEmail, setRequesterEmail] = React.useState<string>('');
  const [files, setFiles] = React.useState<File[]>([]);

  const onChooseFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };
  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!comment.trim()) {
      setSnack({ open: true, msg: t('tickets.hrInquiry.error.comentarioRequerido'), sev: 'error' });
      return;
    }
    setLoading(true);
    try {
      const payload: HrInquiryPayload = {
        type,
        comment: comment.trim(),
        requesterEmail: requesterEmail.trim() || undefined,
        files,
      };

      // Payload preparado

      const res = await createHrInquiry(payload);
      const ticketId = res.id || res.ticketId || '';
      setSnack({
        open: true,
        msg: t('tickets.hrInquiry.success', { ticketId }),
        sev: 'success',
      });
      onSuccess?.(ticketId);

      // reset
      setOpen(false);
      setLoading(false);
      setType('duda');
      setComment('');
      setRequesterEmail('');
      setFiles([]);
    } catch (e: any) {
      const msg = e?.message || t('tickets.hrInquiry.error.crearError');
      setSnack({ open: true, msg, sev: 'error' });
      setLoading(false);
    }
  };

  // ===== Estilos condicionales del botón
  // Para "firmas" hacemos una pastilla amarilla muy similar al botón de Firmas.
  const baseSxDefault = {
    color: '#21a598',
    borderColor: '#21a598',
    '&:hover': {
      backgroundColor: 'rgba(33,165,152,0.08)',
      borderColor: '#1b887f',
      color: '#1b887f',
    },
  };

  const baseSxAppbar = {
    color: '#f9f9f4',
    borderColor: 'rgba(249,249,244,0.6)',
    fontWeight: 700,
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      borderColor: '#f9f9f4',
      color: '#ffffff',
    },
  };

  const baseSxFirmas = {
    // Pastilla amarilla, texto oscuro, misma vibra que el botón "Firmas"
    backgroundColor: '#F5DE59', // amarillo tipo Firmas
    color: '#1b1b1b',
    borderRadius: 9999,
    px: 1.5,
    py: 0.5,
    fontWeight: 700,
    textTransform: 'none' as const,
    border: '1px solid rgba(0,0,0,0.08)',
    '&:hover': {
      backgroundColor: '#EED64B',
    },
  };

  const sxMap = {
    default: baseSxDefault,
    appbar: baseSxAppbar,
    firmas: baseSxFirmas,
  } as const;

  // Ajustes de props para "firmas":
  // - usamos contained + warning para heredar contraste del theme (aunque forzamos sx)
  // - ocultamos el icono para parecerse más al de Firmas
  const computedVariant = appearance === 'firmas' ? 'contained' : variant;
  const computedColor =
    appearance === 'firmas'
      ? ('warning' as const)
      : appearance === 'appbar'
        ? ('inherit' as const)
        : color;

  const startIcon = appearance === 'firmas' ? undefined : <HelpOutlineIcon />;

  return (
    <>
      <Button
        startIcon={startIcon}
        variant={computedVariant}
        color={computedColor as any}
        size={size}
        onClick={() => setOpen(true)}
        title={t('tickets.hrInquiry.title') as string}
        disableElevation
        sx={sxMap[appearance]}
      >
        {buttonText || defaultButtonText}
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tickets.hrInquiry.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="hr-type">{t('tickets.hrInquiry.field.tipo')}</InputLabel>
              <Select
                labelId="hr-type"
                label={t('tickets.hrInquiry.field.tipo')}
                value={type}
                onChange={(e) => setType(e.target.value as any)}
              >
                <MenuItem value="duda">{t('tickets.hrInquiry.tipo.duda')}</MenuItem>
                <MenuItem value="queja">{t('tickets.hrInquiry.tipo.queja')}</MenuItem>
                <MenuItem value="solicitud">{t('tickets.hrInquiry.tipo.solicitud')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={t('tickets.hrInquiry.field.comentario')}
              multiline
              minRows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <Button component="label" variant="text">
                {t('tickets.common.adjuntarArchivos')}
                <input hidden multiple type="file" onChange={onChooseFiles} />
              </Button>
              {files.length > 0 && (
                <small>
                  {t('tickets.hrInquiry.archivosSeleccionados', { count: files.length })}
                </small>
              )}
            </Stack>

            {/* Lista pequeña de archivos con opción de quitar */}
            {files.length > 0 && (
              <Stack spacing={1}>
                {files.map((f, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <small>{f.name}</small>
                    <IconButton
                      size="small"
                      onClick={() => removeFile(i)}
                      title={t('tickets.hrInquiry.quitar') as string}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            {t('tickets.common.cancelar')}
          </Button>
          <Button onClick={submit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : t('tickets.common.crearTicket')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
